import { useMemo } from 'react';

import { useAtomValue } from 'jotai';

import { activeData3dAtom } from '@store';

import { ViewPreset } from '../components';

type SwingPosition = 'P1' | 'P2' | 'P3' | 'P4' | 'P5' | 'P6' | 'P7' | 'P8' | 'P9' | 'P10';

type ViewPresetConfig = {
    azimuthal: number;
    polar: number;
    focus: string | null;
    position?: SwingPosition;
    horizontalOffset?: number;
};

export const VIEW_PRESETS = {
    FACE_ON: 'face_on',
    DOWN_THE_LINE: 'down_the_line',
    TOP: 'top',
    BACKSIDE: 'backside',
} as const;

export const VIEW_PRESET_CONFIG: Record<ViewPreset, ViewPresetConfig> = {
    [VIEW_PRESETS.FACE_ON]: {
        azimuthal: 0,
        polar: Math.PI / 2,
        focus: 'pelvis',
    },
    [VIEW_PRESETS.DOWN_THE_LINE]: {
        azimuthal: -Math.PI / 2,
        polar: Math.PI / 2,
        focus: 'right_wrist',
        position: 'P1',
    },
    [VIEW_PRESETS.TOP]: {
        azimuthal: Math.PI,
        polar: 0.01,
        focus: 'right_wrist',
    },
    [VIEW_PRESETS.BACKSIDE]: {
        azimuthal: Math.PI,
        polar: Math.PI / 2,
        focus: 'pelvis',
    },
};

export function useViewPresetCamera(currentViewPreset: ViewPreset | null) {
    const activeData = useAtomValue(activeData3dAtom);
    const effectiveCameraFocus = useMemo(() => {
        if (currentViewPreset) {
            return VIEW_PRESET_CONFIG[currentViewPreset].focus;
        }
        return null;
    }, [currentViewPreset]);

    const effectiveCameraFocusFrame = useMemo(() => {
        if (currentViewPreset && activeData) {
            const position = VIEW_PRESET_CONFIG[currentViewPreset].position;
            if (position) {
                return activeData.segmentation[position];
            }
        }
        return undefined;
    }, [currentViewPreset, activeData]);

    const effectiveHorizontalOffset = useMemo(() => {
        if (currentViewPreset) {
            return VIEW_PRESET_CONFIG[currentViewPreset].horizontalOffset ?? 0;
        }
        return 0;
    }, [currentViewPreset]);

    return {
        effectiveCameraFocus,
        effectiveCameraFocusFrame,
        effectiveHorizontalOffset,
    };
}
